import { useHealth } from "../features/health/useHealth";
import { StatusPill } from "../components/StatusPill";
import { FlowPreview } from "../features/flow/FlowPreview";
import { useAuth } from "../features/auth/useAuth";
import { SignIn } from "../features/auth/SignIn";
import { AcceptInvite } from "../features/auth/AcceptInvite";
import { ResetPassword } from "../features/auth/ResetPassword";
import { InvitesPanel } from "../features/invites/InvitesPanel";
import { PasswordResetPanel } from "../features/invites/PasswordResetPanel";
import { ReferencesPanel } from "../features/flow/ReferencesPanel";
import { AdhocCallPanel } from "../features/flow/AdhocCallPanel";
import type { Identity } from "../api/client";

/**
 * Application shell.
 *
 * A real sign-in screen gates everything below it (P7): there is no way to
 * see the dashboard without an authenticated session, and the identity shown
 * always comes from the API, never from anything stored client-side.
 *
 * There is no client-side router in this app. An invite or reset link is a
 * plain URL (`/accept-invite?token=...`, `/reset-password?token=...`) that a
 * real deployment would email; reading `window.location` directly for these
 * two fixed paths is a reasonable trade-off for two screens rather than
 * pulling in routing for the whole app. Both render regardless of auth state,
 * since accepting an invite creates a DIFFERENT, new user.
 */
export function App() {
  const path = window.location.pathname;
  const token = new URLSearchParams(window.location.search).get("token") ?? "";

  if (path === "/accept-invite" && token) {
    return <AcceptInvite token={token} />;
  }
  if (path === "/reset-password" && token) {
    return <ResetPassword token={token} />;
  }

  return <AuthGate />;
}

function AuthGate() {
  const { status, identity, login, logout } = useAuth();

  if (status === "checking") {
    return (
      <main className="shell shell--signin">
        <p className="shell__subtitle">Checking session…</p>
      </main>
    );
  }

  if (status === "signed_out" || !identity) {
    return <SignIn onSubmit={login} />;
  }

  return <Dashboard identity={identity} onSignOut={() => void logout()} />;
}

function Dashboard({ identity, onSignOut }: { identity: Identity; onSignOut: () => void }) {
  const { snapshot, refresh } = useHealth();

  return (
    <main className="shell">
      <header className="shell__header">
        <div>
          <h1>Utility Voice IVR</h1>
          <p className="shell__subtitle">
            Control plane {snapshot.version ? `build ${snapshot.version}` : ""}
          </p>
        </div>
        <div className="shell__identity">
          <span className="shell__role">{identity.role}</span>
          <button type="button" onClick={onSignOut}>
            Sign out
          </button>
        </div>
      </header>

      <section aria-labelledby="readiness-heading" className="card">
        <h2 id="readiness-heading">Readiness</h2>
        <dl className="probes">
          <div className="probe">
            <dt>API process</dt>
            <dd>
              <StatusPill state={snapshot.api} />
            </dd>
          </div>
          <div className="probe">
            <dt>Control plane (database)</dt>
            <dd>
              <StatusPill state={snapshot.control} />
            </dd>
          </div>
          <div className="probe">
            <dt>Media plane</dt>
            <dd>
              <StatusPill state={snapshot.media} />
              {snapshot.mediaPlane ? (
                <span className="probe__detail">
                  {snapshot.mediaPlane} · {snapshot.adapter} · {snapshot.instanceId}
                </span>
              ) : null}
            </dd>
          </div>
        </dl>
        <p className="card__note">
          Media readiness is separate from web readiness on purpose: a node that cannot drive
          calls stops admitting them without taking the API offline.
        </p>
        {snapshot.lastError ? <p className="card__error">Last error: {snapshot.lastError}</p> : null}
        <button type="button" onClick={refresh}>
          Check now
        </button>
        {snapshot.lastCheckedAt ? (
          <p className="card__note">Last checked {snapshot.lastCheckedAt.toLocaleTimeString()}</p>
        ) : null}
      </section>

      {identity.role === "tenant_admin" ? (
        <>
          <InvitesPanel />
          <PasswordResetPanel />
        </>
      ) : null}

      {identity.role !== "viewer" ? <ReferencesPanel /> : null}

      {identity.role === "tenant_admin" ? <AdhocCallPanel /> : null}

      <FlowPreview />

      <section aria-labelledby="scope-heading" className="card">
        <h2 id="scope-heading">What this build does</h2>
        <ul>
          <li>Validates flow documents against the schema contract in Go and TypeScript.</li>
          <li>Runs start → play → hangup against the fake media driver.</li>
          <li>Persists calls, attempts, normalized events and one final CDR per attempt.</li>
          <li>Renders a shipped example flow as a graph, lets you drag nodes to reposition them,
            and lets a selected node's config be edited and re-validated (above).</li>
          <li>Adds a new node from a palette (schema-shaped default config) and deletes the
            selected one, cleaning up any edge that pointed at it.</li>
          <li>Drag between two nodes' handles to connect them; a selected edge's own port name
            is editable, and it can be deleted too. Connecting is a single generic handle per
            node, not aware of each node type's real exit ports yet.</li>
          <li>Every node type now gets a real typed config form — including gather, whose
            audio/tts prompt toggle switches which sub-fields show without losing whatever
            was typed into the other shape, and branch's cases, with a real dropdown for the
            comparison operator.</li>
          <li>A dashed amber outline and a warning glyph mark any node whose own required
            exit ports (mirroring the Go executor's real per-node-type port table) aren't
            all wired to an edge, and the diagnostics panel names exactly which port.</li>
          <li>Undo/Redo buttons and a Ctrl/Cmd+Z (Shift to redo) shortcut cover every edit —
            drag, add/remove, connect, an Inspector Apply — and switching the example
            dropdown starts a fresh history rather than carrying the old one over.</li>
          <li>A "Start test call" panel walks the document's own edges by port, pausing at
            gather/branch/repeat/amd_gate/transfer nodes for you to pick an outcome and
            highlighting the current node on the canvas — a structural reachability check,
            not a real execution proof (no TTS, HTTP, AMD or DTMF actually happen).</li>
          <li>Edits autosave to this browser (one slot, remembering which example it started
            from), so a reload restores whatever you were last working on; Reset discards it
            and reloads the canonical fixture.</li>
          <li>A real sign-in screen (above the fold when signed out) authenticates against
            the API with a username and password, and Sign out revokes the session — nothing
            here is a fixture (P7).</li>
          <li>A tenant admin can invite a new username into a role and revoke a still-pending
            invite; the invite link (there is no email integration yet, so it is shown once
            for the admin to relay themselves) sets the invitee's password and signs them
            straight in. A tenant admin can also trigger a password reset for a member of
            their own tenant the same way — deliberately admin-triggered, not a public
            "forgot password" form, since there is no way yet to deliver a reset link only to
            its own account owner (P7).</li>
          <li>Publish validates the current document against this tenant's own real registered
            references (assets, voices — registered just above the designer) and, only if it
            passes, persists it as a new, immutable flow version — a real backend round trip,
            not local-only anymore. A failed publish shows exactly which node's reference is
            unverified rather than a generic error.</li>
          <li>A tenant admin can trigger one real outbound call against an already-published
            flow version ("Place a call", above the designer) — recorded by the API and placed
            by cmd/worker's own dispatcher through the same admission machinery (capacity/CPS)
            every campaign call already shares, with AMD wired to a real, lab-proven heuristic
            detector when the worker is configured for it.</li>
        </ul>
        <h3>Not built yet</h3>
        <ul>
          <li>Inbound DID routing (P3), outbound campaigns' own UI (P4) — publishing a flow
            version does not yet connect it to a DID, and campaigns are store/HTTP-only so far.</li>
          <li>Real email delivery for invites and resets, and a genuine self-service
            "forgot password" flow, which needs it (P7).</li>
          <li>Real asset scanning/transcoding and real Google TTS rendering — registering an
            asset or voice here only proves it's a deliberate tenant choice, which is what
            publish's own validation checks for; nothing renders real audio from either yet.</li>
        </ul>
      </section>
    </main>
  );
}
